import React, { useState, useEffect } from 'react';
import { Card, CardTitle, Insight, MetricCard, Grid } from '../components/UI';
import { supabase } from '../supabaseClient';

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';

const CHANNELS = [
  { id: 'email',    label: 'Email',    color: '#185FA5', bg: '#E6F1FB', icon: 'M2 5h16v12H2z M2 5l8 7 8-7' },
  { id: 'push',     label: 'Push',     color: '#0F6E56', bg: '#E1F5EE', icon: 'M9 2a7 7 0 100 14H9V2z M12 16v2M9 16v2' },
  { id: 'telegram', label: 'Telegram', color: '#185FA5', bg: '#E6F1FB', icon: 'M2 11L16 5l-4 13-4-6-6-2z' },
];

const SEGMENTS = [
  { id: 'all',      label: 'All users' },
  { id: 'active',   label: 'Active pass holders' },
  { id: 'expiring', label: 'Expiring in 30 days' },
  { id: 'trial',    label: 'Trial users' },
];

function ChanIcon({ icon, size = 16, stroke = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 18 18" fill="none"
      stroke={stroke} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      {icon.split(' M').filter(Boolean).map((d, i) => (
        <path key={i} d={'M' + d} fill="none" />
      ))}
    </svg>
  );
}

function StatusBadge({ status }) {
  const cfg = {
    sent:    { bg: '#E1F5EE', color: '#085041', label: 'Sent' },
    partial: { bg: '#FAEEDA', color: '#633806', label: 'Partial' },
    failed:  { bg: '#FCEBEB', color: '#501313', label: 'Failed' },
    sending: { bg: '#E6F1FB', color: '#042C53', label: 'Sending' },
  }[status] || { bg: '#f0f4f5', color: '#9eb8c8', label: status };
  return (
    <span style={{ fontSize: '.62rem', fontWeight: 600, padding: '2px 8px', borderRadius: 8,
      background: cfg.bg, color: cfg.color }}>{cfg.label}</span>
  );
}

export default function CommsHub() {
  const [channels,  setChannels]  = useState({ email: true, push: false, telegram: false });
  const [subject,   setSubject]   = useState('');
  const [message,   setMessage]   = useState('');
  const [segment,   setSegment]   = useState('all');
  const [tgChatId,  setTgChatId]  = useState('');
  const [sending,   setSending]   = useState(false);
  const [results,   setResults]   = useState(null);
  const [history,   setHistory]   = useState([]);
  const [stats,     setStats]     = useState({ emails: 0, pushTokens: 0, tgSubscribers: 0 });

  const activeChannels = CHANNELS.filter(c => channels[c.id]);
  const charCount = message.length;
  const pushOk = charCount <= 178;

  useEffect(() => {
    (async () => {
      try {
        const { count: emails } = await supabase
          .from('user_passes').select('*', { count: 'exact', head: true });
        const { count: push } = await supabase
          .from('push_notifications').select('*', { count: 'exact', head: true }).eq('status', 'sent');
        setStats({ emails: emails || 0, pushTokens: 0, tgSubscribers: 0 });
      } catch (_e) { console.error('CommsHub error:', _e); }

      try {
        const { data } = await supabase
          .from('comms_broadcasts')
          .select('*').order('sent_at', { ascending: false }).limit(20);
        setHistory(data || []);
      } catch (_e) { console.error('CommsHub error:', _e); }
    })();
  }, []);

  const isValid = message.trim().length > 0
    && (!channels.email || subject.trim().length > 0)
    && activeChannels.length > 0;

  async function handleSend() {
    if (!isValid || sending) return;
    setSending(true);
    setResults(null);

    const res = {};
    const base = window.location.origin;

    await Promise.all([
      channels.email && (async () => {
        try {
          const r = await fetch(base + '/api/send-email', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ subject, body: message, segment }),
          });
          res.email = await r.json();
        } catch (e) { res.email = { error: e.message }; }
      })(),

      channels.push && (async () => {
        try {
          const r = await fetch(base + '/api/send-push', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ title: subject || 'Sanoire', body: message.slice(0, 178), tokens: [] }),
          });
          res.push = await r.json();
        } catch (e) { res.push = { error: e.message }; }
      })(),

      channels.telegram && tgChatId && (async () => {
        try {
          const r = await fetch(base + '/api/send-telegram', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ message, chat_ids: [tgChatId] }),
          });
          res.telegram = await r.json();
        } catch (e) { res.telegram = { error: e.message }; }
      })(),
    ].filter(Boolean));

    // Save to broadcast history
    try {
      await supabase.from('comms_broadcasts').insert({
        subject: subject || null,
        message,
        channels: activeChannels.map(c => c.id),
        segment,
        results: res,
        sent_at: new Date().toISOString(),
      });
      const { data } = await supabase
        .from('comms_broadcasts')
        .select('*').order('sent_at', { ascending: false }).limit(20);
      setHistory(data || []);
    } catch (_e) { console.error('CommsHub error:', _e); }

    setResults(res);
    setSending(false);
  }

  return (
    <div style={{ fontFamily: F }}>

      <Grid cols={3}>
        <MetricCard label="Email audience" value={stats.emails} sub="pass holders" />
        <MetricCard label="Push tokens"    value={stats.pushTokens || 0} sub="opted in" />
        <MetricCard label="Broadcasts sent" value={history.length} sub="all time" />
      </Grid>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))', gap: 16 }}>

        {/* Composer */}
        <Card style={{ marginBottom: 0 }}>
          <CardTitle>New broadcast</CardTitle>

          {/* Channel picker */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: '.62rem', fontWeight: 600, color: '#9eb8c8',
              textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 7 }}>Send via</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {CHANNELS.map(ch => (
                <button key={ch.id}
                  onClick={() => setChannels(prev => ({ ...prev, [ch.id]: !prev[ch.id] }))}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '7px 12px', borderRadius: 8, cursor: 'pointer',
                    border: channels[ch.id] ? '2px solid ' + ch.color : '1.5px solid #d8eaf4',
                    background: channels[ch.id] ? ch.bg : '#fafcfd',
                    color: channels[ch.id] ? ch.color : '#9eb8c8',
                    fontFamily: F, fontSize: '.74rem', fontWeight: channels[ch.id] ? 600 : 400,
                    transition: 'all .12s',
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 18 18" fill="none"
                    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    {ch.id === 'email' && <><rect x="2" y="4" width="14" height="10" rx="2"/><path d="M2 4l7 6 7-6"/></>}
                    {ch.id === 'push' && <><path d="M9 2a6 6 0 016 6c0 4-3 6-6 8C6 14 3 12 3 8a6 6 0 016-6z"/><circle cx="9" cy="8" r="2" fill="currentColor"/></>}
                    {ch.id === 'telegram' && <path d="M2 10L15 4l-4 12-4-5-5-1z"/>}
                  </svg>
                  {ch.label}
                  {channels[ch.id] && <span style={{ width: 6, height: 6, borderRadius: '50%',
                    background: ch.color, display: 'inline-block' }} />}
                </button>
              ))}
            </div>
          </div>

          {/* Subject (email only) */}
          {channels.email && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: '.62rem', fontWeight: 600, color: '#9eb8c8',
                textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 4 }}>Subject</div>
              <input value={subject} onChange={e => setSubject(e.target.value)}
                placeholder="Email subject line…"
                style={{ width: '100%', padding: '7px 10px', borderRadius: 8, boxSizing: 'border-box',
                  border: '1px solid #d8eaf4', fontSize: '.8rem', color: '#1a3040',
                  fontFamily: F, outline: 'none' }}
              />
            </div>
          )}

          {/* Message body */}
          <div style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <div style={{ fontSize: '.62rem', fontWeight: 600, color: '#9eb8c8',
                textTransform: 'uppercase', letterSpacing: '.07em' }}>Message</div>
              <div style={{ fontSize: '.62rem', color: channels.push && !pushOk ? '#e05c5c' : '#9eb8c8' }}>
                {charCount}/178 push limit
              </div>
            </div>
            <textarea value={message} onChange={e => setMessage(e.target.value)}
              placeholder="Write your message… use [first_name] to personalise"
              rows={5}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, boxSizing: 'border-box',
                border: '1px solid #d8eaf4', fontSize: '.8rem', color: '#1a3040',
                fontFamily: F, outline: 'none', resize: 'vertical', lineHeight: 1.55 }}
            />
          </div>

          {/* Segment picker */}
          {channels.email && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: '.62rem', fontWeight: 600, color: '#9eb8c8',
                textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 4 }}>Segment</div>
              <select value={segment} onChange={e => setSegment(e.target.value)}
                style={{ width: '100%', padding: '7px 10px', borderRadius: 8, boxSizing: 'border-box',
                  border: '1px solid #d8eaf4', fontSize: '.8rem', color: '#1a3040', fontFamily: F }}>
                {SEGMENTS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
          )}

          {/* Telegram chat ID */}
          {channels.telegram && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: '.62rem', fontWeight: 600, color: '#9eb8c8',
                textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 4 }}>Telegram channel ID</div>
              <input value={tgChatId} onChange={e => setTgChatId(e.target.value)}
                placeholder="e.g. @SanoireChannel or -100123456789"
                style={{ width: '100%', padding: '7px 10px', borderRadius: 8, boxSizing: 'border-box',
                  border: '1px solid #d8eaf4', fontSize: '.8rem', color: '#1a3040',
                  fontFamily: F, outline: 'none' }}
              />
            </div>
          )}

          {/* Push warning */}
          {channels.push && !pushOk && (
            <Insight type="warn" style={{ marginBottom: 10 }}>
              Message is too long for push ({charCount} chars). It will be truncated to 178 chars for push notifications.
            </Insight>
          )}

          {/* Results */}
          {results && (
            <div style={{ marginBottom: 12 }}>
              {Object.entries(results).map(([ch, r]) => (
                <div key={ch} style={{ display: 'flex', alignItems: 'center', gap: 8,
                  padding: '6px 10px', marginBottom: 4, borderRadius: 8,
                  background: r.error ? '#fef0ef' : '#f0fbf5',
                  border: '1px solid ' + (r.error ? '#fcc' : '#b3e6cc') }}>
                  <span style={{ fontSize: '.72rem', fontWeight: 600, color: '#1a3040',
                    textTransform: 'capitalize' }}>{ch}</span>
                  {r.error
                    ? <span style={{ fontSize: '.68rem', color: '#c0392b' }}>{r.error}</span>
                    : <span style={{ fontSize: '.68rem', color: '#1b5e20' }}>
                        {r.sent != null ? r.sent + ' sent' : ''}
                        {r.failed > 0 ? ', ' + r.failed + ' failed' : ''}
                        {r.note ? r.note : ''}
                      </span>
                  }
                </div>
              ))}
            </div>
          )}

          <button onClick={handleSend} disabled={!isValid || sending}
            style={{ width: '100%', padding: '10px', borderRadius: 8,
              background: isValid && !sending ? '#314F5C' : '#d8eaf4',
              color: isValid && !sending ? '#AFE1F6' : '#9eb8c8',
              border: 'none', fontFamily: F, fontSize: '.82rem', fontWeight: 600,
              cursor: isValid && !sending ? 'pointer' : 'default', transition: 'all .15s',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            {sending ? (
              <><span style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid #AFE1F6',
                borderTopColor: 'transparent', borderRadius: '50%',
                animation: 'spin 0.7s linear infinite' }} />Sending to {activeChannels.map(c => c.label).join(' + ')}…</>
            ) : (
              <>Send to {activeChannels.length > 0 ? activeChannels.map(c => c.label).join(' + ') : '—'} →</>
            )}
          </button>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </Card>

        {/* Preview panel */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

          {channels.email && (
            <Card style={{ marginBottom: 0 }}>
              <CardTitle>Email preview</CardTitle>
              <div style={{ border: '1px solid #d8eaf4', borderRadius: 8, overflow: 'hidden', fontSize: '.72rem' }}>
                <div style={{ background: '#314F5C', padding: '10px 14px',
                  color: '#AFE1F6', fontWeight: 700, fontSize: '.8rem', letterSpacing: '-.3px' }}>
                  sanoire<sup style={{ fontSize: '7px', opacity: .7 }}>®</sup>
                </div>
                <div style={{ padding: '12px 14px', color: '#1a3040' }}>
                  <div style={{ fontWeight: 600, marginBottom: 6, fontSize: '.78rem' }}>
                    {subject || <span style={{ color: '#9eb8c8' }}>Subject line…</span>}
                  </div>
                  <div style={{ color: '#314F5C', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                    {message || <span style={{ color: '#9eb8c8' }}>Your message…</span>}
                  </div>
                </div>
                <div style={{ padding: '8px 14px', background: '#f5fafc',
                  borderTop: '1px solid #d8eaf4', fontSize: '.62rem', color: '#9eb8c8' }}>
                  sanoire.com · Unsubscribe
                </div>
              </div>
            </Card>
          )}

          {channels.push && (
            <Card style={{ marginBottom: 0 }}>
              <CardTitle>Push preview</CardTitle>
              <div style={{ background: '#0d1d2b', borderRadius: 10, padding: '12px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: '#314F5C',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '.6rem', fontWeight: 700, color: '#AFE1F6', flexShrink: 0 }}>S</div>
                  <div>
                    <div style={{ fontSize: '.74rem', fontWeight: 600, color: '#e8f4fb', marginBottom: 2 }}>
                      {(subject || 'Sanoire').slice(0, 50)}
                    </div>
                    <div style={{ fontSize: '.68rem', color: 'rgba(175,225,246,0.65)', lineHeight: 1.4 }}>
                      {message.slice(0, 178) || <span style={{ opacity: .4 }}>Message…</span>}
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {channels.telegram && (
            <Card style={{ marginBottom: 0 }}>
              <CardTitle>Telegram preview</CardTitle>
              <div style={{ background: '#f0f6ff', borderRadius: 10, padding: '12px 14px',
                border: '1px solid #d0e6f7' }}>
                <div style={{ fontSize: '.65rem', color: '#5591b8', fontWeight: 600, marginBottom: 4 }}>Sanoire</div>
                <div style={{ fontSize: '.78rem', color: '#1a3040', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
                  {message || <span style={{ color: '#9eb8c8' }}>Your message…</span>}
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Broadcast history */}
      <Card>
        <CardTitle>Broadcast history</CardTitle>
        {history.length === 0 ? (
          <div style={{ fontSize: '.75rem', color: '#9eb8c8' }}>No broadcasts yet. Send your first message above.</div>
        ) : (
          history.map((b, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12,
              padding: '10px 0', borderBottom: '.5px solid rgba(49,79,92,.06)' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                  {(b.channels || []).map(ch => {
                    const c = CHANNELS.find(x => x.id === ch);
                    return c ? (
                      <span key={ch} style={{ fontSize: '.6rem', fontWeight: 600, padding: '1px 6px',
                        borderRadius: 6, background: c.bg, color: c.color }}>{c.label}</span>
                    ) : null;
                  })}
                  <span style={{ fontSize: '.6rem', color: '#9eb8c8' }}>
                    {b.sent_at ? new Date(b.sent_at).toLocaleDateString('en-GB',
                      { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                  </span>
                </div>
                {b.subject && (
                  <div style={{ fontSize: '.74rem', fontWeight: 500, color: '#1a3040', marginBottom: 2 }}>{b.subject}</div>
                )}
                <div style={{ fontSize: '.68rem', color: '#9eb8c8',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 400 }}>
                  {b.message}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {b.results && Object.entries(b.results).map(([ch, r]) => (
                  <StatusBadge key={ch} status={r.error ? 'failed' : r.sent > 0 ? 'sent' : 'partial'} />
                ))}
              </div>
            </div>
          ))
        )}
      </Card>
    </div>
  );
}
